import React, { useState, useEffect, useCallback } from "react";
import { IconComponentProps } from "../interface";
import defaultSvg from "../../assets/icons/svg/default.svg";
import TooltipComponent from "@CubeComponents/html/tooltip";
import { IconButton } from "@mui/material";

const iconModules = import.meta.glob("../../assets/icons/svg/**/*.svg", { import: "default" });

const IconComponent: React.FC<IconComponentProps> = ({
  name,
  className = "",
  isVisible = true,
  isEnabled = true,
  showGradient = false,
  color = null,
  toolTipContent = "",
  position = "top",
  type,
  onClick,
  height = "1.5rem",
  width = "1.5rem",
  menuShow,
  children,
  dataTestId = "",
  parentContainerClass = "",
}: any) => {
  // Sizing source of truth: width/height props (default 1.5rem = 24px). Applied
  // as inline style on the rendered element so they always win over any other
  // classes the caller might add. Authors that want a different size pass
  // width="..." / height="..." rather than Tailwind w-N/h-N classes.
  const [iconSrc, setIconSrc] = useState<string | null | false>(null);

  const initIcon = useCallback(async () => {
    if (name) {
      const key = `../../assets/icons/svg/${name}.svg`;
      const loader: any = iconModules[key];
      if (!loader) {
        setIconSrc(false);
      } else {
        try {
          const src = await loader();
          setIconSrc(src ?? false);
        } catch {
          setIconSrc(false);
        }
      }
    } else {
      setIconSrc(null);
    }
  }, [name]);

  useEffect(() => {
    initIcon();
  }, [name, initIcon]);

  const handleIconClick = (args: any) => {
    if (isEnabled && onClick) {
      onClick(args);
    }
  };
  const iconContent = showGradient ? (
    <div
      style={{
        width,
        height,
        background: color ?? "linear-gradient(to right, #6BBBE3, #3E8AB0)",
        maskImage: `url("${iconSrc}")`,
        maskRepeat: "no-repeat",
        maskSize: "contain",
        WebkitMaskImage: `url("${iconSrc}")`,
        WebkitMaskRepeat: "no-repeat",
        WebkitMaskSize: "contain",
      }}
      className={`${className} ${isEnabled ? "" : "opacity-40"}`}
    />
  ) : (
    <img
      src={iconSrc || defaultSvg}
      alt={name || "default-icon"}
      style={{ width, height }}
      className={`${className} ${isEnabled ? "" : "opacity-40"}`}
    />
  );

  const isInteractive = !!(onClick || toolTipContent);

  const getIcon = () =>
    isInteractive ? (
      <IconButton
        data-testid={dataTestId}
        type={type}
        onClick={handleIconClick}
        disabled={!isEnabled}
        className={parentContainerClass}
        sx={{
          "&.Mui-focusVisible": {
            outline: "0.125rem solid #6BBBE3",
            outlineOffset: "0.125rem",
          },
          "&:hover": {
            backgroundColor: "transparent",
          },
        }}
        style={{
          cursor: isEnabled ? "pointer" : "not-allowed",
          width: "max-content",
          height: "max-content",
          verticalAlign: "top",
          padding: "0",
          display: "flex",
        }}
      >
        {iconContent}
        {children && menuShow && <>{children}</>}
      </IconButton>
    ) : (
      <span
        data-testid={dataTestId}
        className={parentContainerClass}
        style={{
          width: "max-content",
          height: "max-content",
          verticalAlign: "top",
          padding: "0",
          display: "inline-flex",
        }}
      >
        {iconContent}
        {children && menuShow && <>{children}</>}
      </span>
    );
  return isVisible ? (
    <>
      {iconSrc !== null &&
        (toolTipContent ? (
          <TooltipComponent content={toolTipContent} position={position}>
            {getIcon()}
          </TooltipComponent>
        ) : (
          getIcon()
        ))}
    </>
  ) : (
    ""
  );
};

export default IconComponent;
